import { GetThunkAPI } from '@reduxjs/toolkit/dist/createAsyncThunk';
import RequestService, {
    RequestServiceResponse,
} from '@tcc-online/request-service';
import { NotificationActions } from '../../common/actions/NotificationActions';
import CriterionType from '../types/criteria/CriterionType';
import CriterionDisplayOption from '../types/criteria/CriterionDisplayOption';
import IID from '../../types/IID';
import CriterionValueType from '../types/criteria/CriterionValueType';
import AdminProjectType from '../constants/AdminProjectType';
import ImportService from './ImportService';
import { isSuccessfulResponse } from '../../helpers';

const AdminSortingCriteriaService = {
    getCriteria,
    getCriterion,
    updateCriterion,
    createCriterion,
    deleteCriterion,
    getValues,
    createValue,
    updateValue,
    deleteValue,
    importCriteria,
    exportCriteria,
};

interface BasePayload {
    projectId: number;
}

interface ParentValueID {
    parentCriterionValueId?: number;
}

interface ParentID {
    parentCriterionId?: number;
}

interface URLParams extends BasePayload, ParentValueID {
    criterionId?: number;
}

const createUrl = (params: URLParams): string => {
    const { projectId, criterionId, parentCriterionValueId } = params;
    let result: string = `/admin/products/${AdminProjectType.Spokojenost}/projects/${projectId}/criteria`;

    if (criterionId) {
        result += `/${criterionId}`;
    }

    if (parentCriterionValueId) {
        result += `?parentCriterionValueId=${parentCriterionValueId}`;
    }

    return result;
};

interface URLValueParams {
    projectId: number;
    criterionId: number;
    valueId?: number;
    parentCriterionValueId?: number;
}

const createValueUrl = (params: URLValueParams) => {
    const { projectId, criterionId, valueId, parentCriterionValueId } = params;

    let result: string = createUrl({ projectId, criterionId }) + '/values';

    if (valueId) {
        result += `/${valueId}`;
    }

    if (parentCriterionValueId) {
        result += `?parentCriterionValueId=${parentCriterionValueId}`;
    }

    return result;
};

async function getCriteria(
    payload: BasePayload,
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<CriterionType[]>> {
    const { projectId } = payload;
    try {
        return await RequestService.get<CriterionType[]>(
            createUrl({ projectId }),
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se nacist kriteria'),
        ); // TODO: translate

        throw e;
    }
}

async function getCriterion(
    payload: BasePayload & IID,
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<CriterionType>> {
    const { projectId, id } = payload;
    try {
        return await RequestService.get<CriterionType>(
            createUrl({ projectId, criterionId: id }),
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se nacist kriterium'),
        ); // TODO: translate

        throw e;
    }
}

interface BaseUpdatePayload {
    order?: number;
    displayOption?: CriterionDisplayOption;
    name?: string;
    mandatory?: boolean;
}

export interface UpdatePayload
    extends BaseUpdatePayload, BasePayload, IID, ParentValueID, ParentID {}

async function updateCriterion(
    payload: UpdatePayload,
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<CriterionType>> {
    const { projectId, id, order, displayOption, name } = payload;
    try {
        return await RequestService.patch<BaseUpdatePayload, CriterionType>(
            createUrl({ projectId, criterionId: id }),
            { order, displayOption, name },
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se upravit kriterium'),
        ); // TODO: translate

        throw e;
    }
}

interface BaseCreatePayload {
    displayOption: CriterionDisplayOption;
    name: string;
    mandatory: boolean;
}

export interface CreatePayload
    extends BaseCreatePayload, BasePayload, ParentValueID, ParentID {
    parentCriterionValue?: CriterionValueType;
}

async function createCriterion(
    payload: CreatePayload,
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<CriterionType>> {
    const {
        projectId,
        displayOption,
        name,
        mandatory,
        parentCriterionValueId,
    } = payload;
    try {
        return await RequestService.post<BaseCreatePayload, CriterionType>(
            createUrl({ projectId, parentCriterionValueId }),
            { displayOption, name, mandatory },
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se vytvorit kriterium'),
        ); // TODO: translate

        throw e;
    }
}

export interface DeletePayload extends BasePayload, IID, ParentID {}

async function deleteCriterion(
    payload: DeletePayload,
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse> {
    const { projectId, id } = payload;
    try {
        return await RequestService.delete(
            createUrl({ projectId, criterionId: id }),
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se smazat kriterium'),
        ); // TODO: translate

        throw e;
    }
}

interface BaseValuePayload extends BasePayload {
    criterionId: number;
}

interface CreateValuePayload {
    value: string;
}

async function createValue(
    payload: CreateValuePayload &
        BaseValuePayload & { parentCriterionValueId?: number },
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<CriterionValueType>> {
    const { projectId, criterionId, value, parentCriterionValueId } = payload;
    try {
        return await RequestService.post<
            CreateValuePayload,
            CriterionValueType
        >(createValueUrl({ projectId, criterionId, parentCriterionValueId }), {
            value,
        });
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se vytvorit hodnotu'),
        ); // TODO: translate

        throw e;
    }
}

async function getValues(
    payload: ParentValueID & BaseValuePayload,
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<CriterionValueType[]>> {
    const { projectId, criterionId, parentCriterionValueId } = payload;
    try {
        return await RequestService.get<CriterionValueType[]>(
            createValueUrl({ projectId, criterionId, parentCriterionValueId }),
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se nacist hodnoty'),
        ); // TODO: translate

        throw e;
    }
}

interface BaseUpdateValuePayload {
    value?: string;
    order?: number;
}

export interface UpdateValuePayload
    extends BaseUpdateValuePayload, BaseValuePayload, IID, ParentID {}

async function updateValue(
    payload: UpdateValuePayload,
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<CriterionValueType>> {
    const { projectId, criterionId, id, value, order } = payload;
    try {
        return await RequestService.patch<
            BaseUpdateValuePayload,
            CriterionValueType
        >(createValueUrl({ projectId, criterionId, valueId: id }), {
            value,
            order,
        });
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se upravit hodnotu'),
        ); // TODO: translate

        throw e;
    }
}

async function deleteValue(
    payload: BaseValuePayload & IID,
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse> {
    const { projectId, criterionId, id } = payload;
    try {
        return await RequestService.delete(
            createValueUrl({ projectId, criterionId, valueId: id }),
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se smazat hodnotu'),
        ); // TODO: translate

        throw e;
    }
}

async function importCriteria(
    payload: { projectId: number; data: FormData },
    thunkApi: GetThunkAPI<any>,
) {
    const { projectId, data } = payload;

    try {
        const result = await RequestService.post<FormData>(
            `${createUrl({ projectId })}/import`,
            data,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );

        if (isSuccessfulResponse(result.status)) {
            ImportService.saveImportId(result.data.importId);
        }

        return result;
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se importovat kriteria'),
        ); // TODO: translate

        throw e;
    }
}

async function exportCriteria(projectId: number, thunkApi: GetThunkAPI<any>) {
    try {
        return await RequestService.get(
            `${createUrl({ projectId })}/export`,
            {},
            { responseType: 'blob' },
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se importovat kriteria'),
        ); // TODO: translate

        throw e;
    }
}

export default AdminSortingCriteriaService;
